import { useSlotId } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { mergeProps, useObjectRef } from 'react-aria';

import { type NavigationHookProps, type NavigationResult, useNavigationProps } from '../Router';
import { CardProvider, filterCardDOMProps } from './Card';
import { type CardButtonHookProps, type CardButtonResult, useCardButton } from './CardButton';
import { CardDismissButton } from './CardDismissButton';
import { usePressableCardKeyboardNavigation } from './usePressableCardKeyboardNavigation';

export type CardLinkHookProps = Omit<CardButtonHookProps, 'elementType'> & NavigationHookProps;

export type CardLinkResult = CardButtonResult & NavigationResult;

export type CardLinkProps = Omit<CardLinkHookProps, 'headingId' | 'descriptionId'> &
    Omit<React.ComponentPropsWithoutRef<'a'>, 'href'>;

/**
 * Card link component to use a card as a link.
 */
export function CardLink({
    ref,
    disabled,
    onPress,
    onPressStart,
    preventFocusOnPress,
    className,
    routerOptions,
    href,
    onClick,
    kind,
    ...props
}: CardLinkProps & { ref?: React.ForwardedRef<HTMLAnchorElement> }) {
    const rootRef = useObjectRef(ref);
    const headingId = useSlotId();
    const descriptionId = useSlotId();
    const domProps = filterCardDOMProps(props, { isLink: true });
    const keyboardNavigationProps = usePressableCardKeyboardNavigation(rootRef);
    const cardLinkProps = useCardLink({
        onPress,
        onPressStart,
        preventFocusOnPress,
        disabled,
        className,
        routerOptions,
        href,
        kind,
        download: props.download,
        target: props.target,
        onClick,
        headingId,
        descriptionId,
    });

    return (
        <CardProvider headingId={headingId} descriptionId={descriptionId}>
            <a ref={rootRef} {...mergeProps(domProps, cardLinkProps, keyboardNavigationProps)}>
                {props.onDismiss ? <CardDismissButton onDismiss={props.onDismiss} /> : null}
                {props.children}
            </a>
        </CardProvider>
    );
}

/**
 * Low-level hook to create a card button component.
 */
export function useCardLink(props: CardLinkHookProps): CardLinkResult {
    const navigationProps = useNavigationProps(props);
    const cardButtonProps = useCardButton({ ...props, elementType: 'a' });
    return {
        ...mergeProps(navigationProps, cardButtonProps),
        // Remove default "a" tag styles
        className: clsx(cardButtonProps.className, 'text-base no-underline'),
    };
}
